import { Component, Input } from '@angular/core';
import { CdmHealthScreeningRow, CdmPlanRow } from '../../services/cdm-plan/cdm-plan.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
    standalone: false,
    selector: 'cva-cdm-plan-modal',
    templateUrl: './cdm-plan-modal.component.html',
    styleUrl: './cdm-plan-modal.component.scss',
    
})
export class CdmPlanModalComponent {
  @Input() title: string = 'Chronic Condition Management';
  @Input() showTitleIcon: boolean = false;
  @Input() showEligibilityLabel: boolean = true;

  @Input() patientName: string = '';
  @Input() patientDob: string = '';
  @Input() patientMrn: string = '';
  @Input() patientGender: string = '';
  @Input() patientId: string = '';

  @Input() isLoading: boolean = false;
  @Input() errorMessage: string = '';
  @Input() rawResponse: unknown = null;
  @Input() eligibility: string = '';
  @Input() eligibilityReason: string = '';
  @Input() annualSmartGoal: string = '—';
  @Input() planRows: CdmPlanRow[] = [];
  @Input() screeningRows: CdmHealthScreeningRow[] = [];

  copyState: 'idle' | 'copied' | 'error' = 'idle';
  pdfState: 'idle' | 'exporting' | 'error' = 'idle';
  isLIC = localStorage.getItem('isLIC') === 'true';

  trackByIndex(index: number): number {
    return index;
  }

  trackByScreeningAction(_index: number, item: CdmHealthScreeningRow): string {
    return item.action;
  }

  get canCopy(): boolean {
    return !this.isLoading && !this.errorMessage && !!this.rawResponse;
  }

  get canExport(): boolean {
    return this.canCopy && this.pdfState !== 'exporting';
  }

  async copyCdmPlanToClipboard(): Promise<void> {
    if (!this.canCopy) {
      return;
    }

    const html = this.buildClipboardHtml();
    const text = this.buildClipboardText();

    try {
      if (navigator.clipboard && 'ClipboardItem' in window) {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        this.copyFallback(text);
      }

      this.copyState = 'copied';
    } catch {
      try {
        this.copyFallback(text);
        this.copyState = 'copied';
      } catch {
        this.copyState = 'error';
      }
    }

    window.setTimeout(() => {
      this.copyState = 'idle';
    }, 2000);
  }

  exportCdmPlanToPdf(): void {
    if (!this.canExport) {
      return;
    }

    this.pdfState = 'exporting';

    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 14;

      let cursorY = this.renderPdfHeader(doc, pageW, margin);
      cursorY = this.renderEligibility(doc, margin, cursorY);
      cursorY = this.renderSmartGoal(doc, pageW, margin, cursorY);
      cursorY = this.renderPlanTable(doc, margin, cursorY);
      cursorY = this.renderScreeningHeading(doc, margin, cursorY);
      this.renderScreeningTable(doc, margin, cursorY);

      const safeTitle = (this.title || 'Chronic Condition Management').replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
      doc.save(`${safeTitle}.pdf`);

      this.pdfState = 'idle';
    } catch (err) {
      console.error('[CDM Modal] PDF export failed:', err);
      this.pdfState = 'error';
      window.setTimeout(() => {
        this.pdfState = 'idle';
      }, 2000);
    }
  }

  private renderPdfHeader(doc: jsPDF, pageW: number, margin: number): number {
    let cursorY = margin;
    const rightColX = pageW / 2 + 10;
    const rightColW = pageW - margin - rightColX;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(this.title || 'Chronic Condition Management', margin, cursorY);

    const hasPatientInfo = !!(this.patientName || this.patientDob || this.patientMrn || this.patientGender || this.patientId);
    if (hasPatientInfo) {
      this.renderPatientInfo(doc, rightColX, rightColW, cursorY);
    }

    cursorY += hasPatientInfo ? 12 : 8;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(margin, cursorY, pageW - margin, cursorY);
    return cursorY + 6;
  }

  private renderPatientInfo(doc: jsPDF, rightColX: number, rightColW: number, cursorY: number): void {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);

    const rawName = this.patientName || 'Unknown Patient';
    const displayName = this.fitTextToWidth(doc, rawName, rightColW, 4);
    doc.text(displayName, rightColX, cursorY);

    const detailItems = this.getPatientDetailItems();
    if (!detailItems.length) {
      return;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const rawDetails = detailItems.join(' | ');
    const displayDetails = this.fitTextToWidth(doc, rawDetails, rightColW, 6);
    doc.text(displayDetails, rightColX, cursorY + 5);
  }

  private getPatientDetailItems(): string[] {
    const detailItems: string[] = [];
    if (this.patientMrn) {
      detailItems.push(`MRN: ${this.patientMrn}`);
    }
    if (this.patientGender) {
      detailItems.push(`Gender: ${this.patientGender}`);
    }
    if (this.patientDob) {
      detailItems.push(`DOB: ${this.patientDob}`);
    }
    return detailItems;
  }

  private fitTextToWidth(doc: jsPDF, value: string, maxWidth: number, minLength: number): string {
    let displayValue = value;
    while (displayValue.length > minLength && doc.getTextWidth(displayValue) > maxWidth) {
      displayValue = displayValue.slice(0, -1);
    }

    if (displayValue !== value) {
      return `${displayValue.trimEnd()}...`;
    }

    return displayValue;
  }

  private renderEligibility(doc: jsPDF, margin: number, cursorY: number): number {
    if (!this.eligibility) {
      return cursorY;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('CDM Eligibility: ', margin, cursorY);
    const labelW = doc.getTextWidth('CDM Eligibility: ');
    doc.setFont('helvetica', 'normal');
    doc.text(this.eligibility, margin + labelW, cursorY);
    return cursorY + 6;
  }

  private renderSmartGoal(doc: jsPDF, pageW: number, margin: number, cursorY: number): number {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const goalLabel = "Patient's SMART goal for their health within the next 12 months:";
    doc.text(goalLabel, margin, cursorY);

    doc.setFont('helvetica', 'normal');
    const goalLines = doc.splitTextToSize(this.annualSmartGoal || '—', pageW - margin * 2);
    doc.text(goalLines, margin, cursorY + 5);
    return cursorY + goalLines.length * 5 + 9;
  }

  private renderPlanTable(doc: jsPDF, margin: number, cursorY: number): number {
    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [[
        'Relevant conditions in this plan',
        'Patient lifestyle goals - To be achieved',
        'Provider treatments & patient actions',
        'Review 3-6 monthly. Have goals been met? Any further actions required?',
      ]],
      body: this.planRows.length
        ? this.planRows.map((r) => [
            this.normalizeText(r.relevantConditions),
            this.normalizeText(r.patientLifestyleGoals),
            this.normalizeText(r.providerTreatmentsAndActions),
            this.normalizeText(r.reviewNotes),
          ])
        : [['No rows available', '', '', '']],
      theme: 'grid',
      headStyles: { fillColor: [42, 61, 143], textColor: 255, fontSize: 9, fontStyle: 'bold', lineColor: [255, 255, 255], lineWidth: 0.3 },
      bodyStyles: { fontSize: 9, lineColor: [200, 200, 200], lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      columnStyles: { 0: { cellWidth: 34 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 'auto' }, 3: { cellWidth: 30 } },
    });

    return ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? cursorY + 20) + 8;
  }

  private renderScreeningHeading(doc: jsPDF, margin: number, cursorY: number): number {
    if (cursorY > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      cursorY = margin;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Health Prevention Screening:', margin, cursorY);
    return cursorY + 6;
  }

  private renderScreeningTable(doc: jsPDF, margin: number, cursorY: number): void {
    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [['Action', 'Last completed']],
      body: this.screeningRows.length
        ? this.screeningRows.map((r) => [
            this.normalizeText(r.action),
            this.normalizeText(r.lastCompleted),
          ])
        : [['No rows available', '']],
      theme: 'grid',
      headStyles: { fillColor: [42, 61, 143], textColor: 255, fontSize: 9, fontStyle: 'bold', lineColor: [255, 255, 255], lineWidth: 0.3 },
      bodyStyles: { fontSize: 9, lineColor: [200, 200, 200], lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: 'auto' } },
    });
  }

  private buildClipboardHtml(): string {
    const goal = this.escapeHtml(this.annualSmartGoal || '—');
    const eligibility = this.escapeHtml(this.eligibility || '—');

    const planRowsHtml = this.planRows
      .map((row) => `
        <tr>
          <td>${this.escapeHtml(row.relevantConditions)}</td>
          <td>${this.escapeHtml(row.patientLifestyleGoals)}</td>
          <td>${this.escapeHtml(row.providerTreatmentsAndActions)}</td>
          <td>${this.escapeHtml(row.reviewNotes)}</td>
        </tr>`)
      .join('');

    const screeningRowsHtml = this.screeningRows
      .map((row) => `
        <tr>
          <td>${this.escapeHtml(row.action)}</td>
          <td>${this.escapeHtml(row.lastCompleted)}</td>
        </tr>`)
      .join('');

    return `
      <div>
        <h3>${this.escapeHtml(this.title)}</h3>
        <p><strong>CDM Eligibility:</strong> ${eligibility}</p>
        <p><strong>Patient's SMART goal for their health within the next 12 months:</strong> ${goal}</p>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;table-layout:fixed;">
          <thead>
            <tr>
              <th>Relevant conditions in this plan</th>
              <th>Patient lifestyle goals - To be achieved</th>
              <th>Provider treatments &amp; patient actions</th>
              <th>Review 3-6 monthly. Have goals been met? Any further actions required?</th>
            </tr>
          </thead>
          <tbody>${planRowsHtml || '<tr><td colspan="4">No rows available</td></tr>'}</tbody>
        </table>
        <p><strong>Health Prevention Screening:</strong></p>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;table-layout:fixed;">
          <thead>
            <tr>
              <th>Action</th>
              <th>Last completed</th>
            </tr>
          </thead>
          <tbody>${screeningRowsHtml || '<tr><td colspan="2">No rows available</td></tr>'}</tbody>
        </table>
      </div>`;
  }



  private buildClipboardText(): string {
    const lines: string[] = [];

    lines.push(this.title);
    lines.push(`CDM Eligibility:\t${this.eligibility || '—'}`);
    lines.push(`Patient's SMART goal for their health within the next 12 months:\t${this.annualSmartGoal || '—'}`);
    lines.push('');
    lines.push([
      'Relevant conditions in this plan',
      'Patient lifestyle goals - To be achieved',
      'Provider treatments & patient actions',
      'Review 3-6 monthly. Have goals been met? Any further actions required?'
    ].join('\t'));

    if (this.planRows.length === 0) {
      lines.push('No rows available\t\t\t');
    } else {
      this.planRows.forEach((row) => {
        lines.push([
          this.normalizeText(row.relevantConditions),
          this.normalizeText(row.patientLifestyleGoals),
          this.normalizeText(row.providerTreatmentsAndActions),
          this.normalizeText(row.reviewNotes),
        ].join('\t'));
      });
    }

    lines.push('');
    lines.push('Health Prevention Screening');
    lines.push(['Action', 'Last completed'].join('\t'));

    if (this.screeningRows.length === 0) {
      lines.push('No rows available\t');
    } else {
      this.screeningRows.forEach((row) => {
        lines.push([
          this.normalizeText(row.action),
          this.normalizeText(row.lastCompleted),
        ].join('\t'));
      });
    }

    return lines.join('\n');
  }

  private copyFallback(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }

  private normalizeText(value: string | null | undefined): string {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  private escapeHtml(value: string | null | undefined): string {
    return this.normalizeText(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
